import type { MedicationOrderContext, MedicationOrderAlertRef } from '../types/medicationAI'
import type { Patient } from '../types/patient'
import type { PrescriptionMedication } from '../types/prescription'
import type { FormularyAlternativeReference } from '../services/formularyService'
import { calculateAge } from './ageUtils'

function getInteractionId(medication1Name: string, medication2Name: string, index: number): string {
  return `interaction-${medication1Name}-${medication2Name}-${index}`
}

/**
 * Build de-identified medication-order context for adjunct AI (no PII).
 */
export function buildMedicationOrderContext(
  patient: Patient | undefined,
  medications: PrescriptionMedication[],
  allergyCheck: { hasAllergies?: boolean; allergies?: Array<{ allergyId: string; triggerDisplay?: string; medication?: { name?: string } }> } | null,
  interactionCheck: {
    hasInteractions?: boolean
    interactions?: Array<{ medication1?: { name?: string }; medication2?: { name?: string }; severity?: string; description?: string }>
  } | null,
  dosageValidation: {
    hasWarnings?: boolean
    warnings?: Array<{ type?: string; message?: string; medication?: string }>
  } | null,
  formularyAlternatives: FormularyAlternativeReference[],
): MedicationOrderContext {
  const patientAge = patient?.dateOfBirth ? calculateAge(patient.dateOfBirth).years : undefined

  const systemAlerts: MedicationOrderAlertRef[] = []

  if (allergyCheck?.hasAllergies && allergyCheck.allergies) {
    allergyCheck.allergies.forEach((a) => {
      systemAlerts.push({
        id: `allergy-${a.allergyId}`,
        kind: 'allergy',
        severity: 'major',
        summary: `Allergy: ${a.triggerDisplay || 'unknown trigger'} vs ${a.medication?.name || 'ordered drug'}`,
      })
    })
  }

  if (interactionCheck?.hasInteractions && interactionCheck.interactions) {
    interactionCheck.interactions.forEach((i, idx) => {
      const id = getInteractionId(
        i.medication1?.name || 'med1',
        i.medication2?.name || 'med2',
        idx,
      )
      systemAlerts.push({
        id,
        kind: 'interaction',
        severity: i.severity,
        summary: `${i.medication1?.name || '?'} + ${i.medication2?.name || '?'}: ${i.description || 'Potential interaction'}`,
      })
    })
  }

  if (dosageValidation?.hasWarnings && dosageValidation.warnings) {
    dosageValidation.warnings.forEach((w, idx) => {
      systemAlerts.push({
        id: `dosage-${idx}`,
        kind: 'dosage',
        severity: w.type,
        summary: `${w.medication || 'Medication'}: ${w.message || 'Dosage warning'}`,
      })
    })
  }

  return {
    patientAge: patientAge ?? undefined,
    patientGender: patient?.gender,
    chronicConditions: patient?.chronicConditions ?? undefined,
    allergies: patient?.allergies ?? undefined,
    currentMedications: patient?.currentMedications ?? undefined,
    proposedMedications: medications.map((m) => ({
      name: m.name,
      dose: m.dose,
      indication: m.indication,
      referenceDrugId: m.referenceDrugId,
    })),
    systemAlerts: systemAlerts.length ? systemAlerts : undefined,
    formularyAlternatives:
      formularyAlternatives.length > 0
        ? formularyAlternatives.map((a) => ({
            referenceDrugId: a.drugIdPK,
            productName: a.productName,
          }))
        : undefined,
  }
}
